import { observer } from 'mobx-react-lite'
import { useDialogs } from 'hooks/dialogs'
import { useCallback, useEffect, useMemo } from 'react'
import { reaction } from 'mobx'
import { useBreakoutUser } from 'hooks/profile'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import {
  EnrollWithSectionPassDialog,
  PurchaseOrEnrollDialog,
  RedeemCouponDialog,
  SelectCouponOrStripeDialog,
} from './dialogs/PurchaseOrEnrollDialog'
import classNames from 'classnames'
import { SectionPaymentMethod } from '@breakoutlearning/firebase-repository/types'
import { useRootStore } from 'hooks/rootStore'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { useTranslationTyped } from 'i18n/i18n'

export const StudentAssignmentEnroll = observer(
  function StudentAssignmentEnroll() {
    const cubit = useStudentAssignmentCubit()
    const { showDialog, dialogStore } = useDialogs()
    const { tt } = useTranslationTyped()
    const user = useBreakoutUser()
    const slideDeckPrice = cubit.slideDeck.data.slideDeckPrice
    const tokensAvailable = user.availableTokens
    const tokensRequired = slideDeckPrice - user.availableTokens
    const useSectionPassPricing =
      cubit.section.usesSectionPass &&
      cubit.sectionPassPricing.sectionPassPriceInt > 0

    // TODO!: Fix issue where the modal reappears after X seconds?

    // TODO: Do we need to be sensitive to this if useSectionPassPricing is true?
    const rootStore = useRootStore()
    const showPaymentLoading =
      rootStore.router.queryParams?.payment === 'success'

    const buttonLabel = useMemo(() => {
      const { unitType, amount, amountStr } = cubit.assignmentPurchasePrice

      const getEnrollmentStr = (price: string) =>
        tt.student_assignment.enroll_price({ price })

      // cents for section pass pricing
      if (unitType === 'cent') {
        if (amount === 0) return getEnrollmentStr(tt.student_assignment.free())
        return getEnrollmentStr(
          tt.student_library.dollars({ dollars: amountStr })
        )
      }

      // if not cents, must be tokens
      return getEnrollmentStr(
        tt.student_library.token_number({ count: amount })
      )
    }, [
      cubit.assignmentPurchasePrice,
      tt.student_assignment,
      tt.student_library,
    ])

    const showPurchaseOrEnrollDialog = useCallback(
      ({
        forceCoupon,
        forceStripe,
        event,
      }: {
        forceCoupon?: boolean
        forceStripe?: boolean
        event?: React.MouseEvent | React.KeyboardEvent
      } = {}) => {
        showDialog(
          ({ remove }) => {
            const { usesStripePurchase, usesCouponCodes } = cubit.section

            if (useSectionPassPricing)
              return (
                <EnrollWithSectionPassDialog
                  cubit={cubit}
                  usesStripePurchase={usesStripePurchase}
                  usesCouponCodes={usesCouponCodes}
                  closeSelf={remove}
                />
              )

            if (forceStripe && usesStripePurchase)
              return <PurchaseOrEnrollDialog cubit={cubit} closeSelf={remove} />

            if (forceCoupon && usesCouponCodes)
              return <RedeemCouponDialog cubit={cubit} closeSelf={remove} />

            if (usesStripePurchase && usesCouponCodes) {
              if (showPaymentLoading)
                return (
                  <PurchaseOrEnrollDialog cubit={cubit} closeSelf={remove} />
                )
              return (
                <SelectCouponOrStripeDialog
                  cubit={cubit}
                  showStripeDialog={() =>
                    showDialog(
                      ({ remove: removeInner }) => (
                        <PurchaseOrEnrollDialog
                          cubit={cubit}
                          closeSelf={removeInner}
                        />
                      ),
                      { trigger: event?.currentTarget }
                    )
                  }
                  showCouponDialog={() =>
                    showDialog(
                      ({ remove: removeInner }) => (
                        <RedeemCouponDialog
                          cubit={cubit}
                          closeSelf={removeInner}
                        />
                      ),
                      { trigger: event?.currentTarget }
                    )
                  }
                  closeSelf={remove}
                />
              )
            }

            if (usesStripePurchase)
              return <PurchaseOrEnrollDialog cubit={cubit} closeSelf={remove} />

            return <RedeemCouponDialog cubit={cubit} closeSelf={remove} />
          },
          {
            trigger: event?.currentTarget,
          }
        )
      },
      [showDialog, cubit, useSectionPassPricing, showPaymentLoading]
    )

    useEffect(() => {
      return reaction(
        () => ({
          canPopEnrollDialog:
            cubit.canPopEnrollDialog && dialogStore.dialogs.length === 0,
          tokensAvailable: user.availableTokens,
          tokensLoading: user.tokensLoading,
        }),
        ({ canPopEnrollDialog, tokensAvailable, tokensLoading }, prev) => {
          // if tokens are still loading, don't proceed as available will be 0
          if (tokensLoading || !canPopEnrollDialog) return
          const didTokensUpdate = tokensAvailable !== prev?.tokensAvailable
          const shouldPop =
            canPopEnrollDialog !== prev?.canPopEnrollDialog || didTokensUpdate
          if (!shouldPop) return
          showPurchaseOrEnrollDialog()
        },
        { fireImmediately: true }
      )
    }, [dialogStore, user, cubit, showDialog, showPurchaseOrEnrollDialog])

    return (
      <div
        data-testid="student-enrollment-section"
        className="flex h-full flex-col items-center justify-center rounded-3xl bg-core-tertiary px-20"
      >
        <h1 className="mb-[4px] text-title-large">
          {tt.student_assignment.enroll_in_case()}
        </h1>
        <p
          data-testid="enrollment-description"
          className="mb-[16px] text-center text-body-medium"
        >
          {useSectionPassPricing
            ? tt.student_assignment.section_pass_description()
            : tt.student_assignment.token_description()}
        </p>
        <div className="flex w-full flex-row justify-center gap-3">
          {!useSectionPassPricing && cubit.section.usesStripePurchase && (
            <BreakoutButton
              data-testid="enroll-button"
              size="large"
              kind="primary"
              className={classNames({
                // if both buttons render, try and match width
                'min-w-[165px]':
                  cubit.section.data.sectionPaymentMethod ===
                  SectionPaymentMethod.sectionPassStripeCoupon,
              })}
              onClick={(event) =>
                showPurchaseOrEnrollDialog({ forceStripe: true, event })
              }
            >
              {buttonLabel}
            </BreakoutButton>
          )}
          {!useSectionPassPricing && cubit.section.usesCouponCodes && (
            <BreakoutButton
              data-testid="redeem-coupon-button"
              size="large"
              kind="primary"
              className={classNames({
                // if both buttons render, try and match width
                'min-w-[165px]':
                  cubit.section.data.sectionPaymentMethod ===
                  SectionPaymentMethod.sectionPassStripeCoupon,
              })}
              onClick={(event) =>
                showPurchaseOrEnrollDialog({ forceCoupon: true, event })
              }
            >
              {tt.student_assignment.redeem_coupon()}
            </BreakoutButton>
          )}
          {useSectionPassPricing && (
            <BreakoutButton
              data-testid="enroll-button"
              size="large"
              kind="primary"
              className={classNames({
                // if both buttons render, try and match width
                'min-w-[165px]':
                  cubit.section.data.sectionPaymentMethod ===
                  SectionPaymentMethod.sectionPassStripeCoupon,
              })}
              onClick={(event) => showPurchaseOrEnrollDialog({ event })}
            >
              {buttonLabel}
            </BreakoutButton>
          )}
        </div>
        {!useSectionPassPricing && (
          <div className="mt-1 flex items-center">
            <p className="mr-1 text-body-medium">
              {tt.student_assignment.tokens_available_colon()}
            </p>
            <p
              className={`text-label-medium ${
                tokensRequired > 0 ? 'text-red-500' : '' // Change text color conditionally
              }`}
            >
              {tt.student_library.token_number({ count: tokensAvailable })}
            </p>
          </div>
        )}
      </div>
    )
  }
)
